import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PlanSelector from '@/components/PlanSelector';
import { StatsCard, WelcomeCard, DashboardHeader } from '@/components/student';
import { 
  Lock, 
  CheckCircle, 
  Circle, 
  ChevronRight, 
  BookOpen,
  TrendingUp,
  Award,
  Clock,
  PlayCircle,
  BarChart3,
  User,
  LogOut,
  Home,
  Menu,
  X,
  Gift,
  FileText,
  Calendar,
  Settings
} from 'lucide-react';

const StudentDashboard = ({ user, onLogout, refreshUser }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data states
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [coursePricingPlans, setCoursePricingPlans] = useState({}); // Store plans by course ID
  const [freeTests, setFreeTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseStructure, setCourseStructure] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentCourse, setPaymentCourse] = useState(null);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [viewCourse, setViewCourse] = useState(null); // For course details modal
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      await Promise.all([
        fetchDepartments(),
        fetchCourses(),
        fetchEnrolledCourses(),
        fetchFreeTests(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      const enabledCourses = response.data.filter(c => c.enabled);
      setCourses(enabledCourses);
      
      // Fetch pricing plans for each course
      for (const course of enabledCourses) {
        fetchCoursePricingPlans(course.id);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchCoursePricingPlans = async (courseId) => {
    try {
      const response = await axios.get(`/pricing-plans/${courseId}`);
      setCoursePricingPlans(prev => ({ ...prev, [courseId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
    }
  };

  const fetchCoursesByDepartment = async (departmentId) => {
    try {
      const response = await axios.get(`/departments/${departmentId}/courses`);
      const enabledCourses = response.data.filter(c => c.enabled);
      setCourses(enabledCourses);
      setSelectedDepartment(departmentId);
      
      // Fetch pricing plans for each course
      for (const course of enabledCourses) {
        fetchCoursePricingPlans(course.id);
      }
    } catch (error) {
      console.error('Failed to fetch courses by department:', error);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const response = await axios.get('/my-courses');
      setEnrolledCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch enrolled courses:', error);
    }
  };

  const fetchFreeTests = async () => {
    try {
      const response = await axios.get('/free-tests');
      setFreeTests(response.data);
    } catch (error) {
      console.error('Failed to fetch free tests:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/student/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Set default stats if API fails
      setStats({
        coursesEnrolled: enrolledCourses.length,
        testsCompleted: 0,
        testsPending: 0,
        averageScore: 0
      });
    }
  };

  const fetchCourseStructure = async (courseId) => {
    setSelectedCourse(courseId);
    setCourseStructure(null); // Reset first
    try {
      const response = await axios.get(`/courses/${courseId}/structure`);
      console.log('Course structure loaded:', response.data);
      setCourseStructure(response.data);
    } catch (error) {
      console.error('Failed to fetch course structure:', error);
      toast.error('Failed to load course details');
      setSelectedCourse(null); // Close dialog on error
    }
  };

  const handlePurchaseCourse = async (course) => {
    setPaymentCourse(course);
    setShowCourseDetails(false);
    setShowPayment(true);
  };

  const handleViewCourseDetails = (course) => {
    setViewCourse(course);
    setShowCourseDetails(true);
  };

  const handleViewEnrolledCourse = (courseId) => {
    setShowCourseDetails(false);
    fetchCourseStructure(courseId);
    setActiveTab('my-courses');
  };

  const handlePayment = async (planData) => {
    setLoading(true);
    try {
      // Create Razorpay order
      const orderResponse = await axios.post('/payment/create-order', {
        course_id: paymentCourse.id,
        pricing_plan_id: planData?.pricing_plan_id,
        coupon_code: planData?.coupon_code
      });

      const orderData = orderResponse.data;
      
      // If Razorpay is configured (is_live = true), open Razorpay checkout
      if (orderData.is_live && orderData.key_id) {
        // Load Razorpay script if not already loaded
        if (!window.Razorpay) {
          await loadRazorpayScript();
        }
        
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'DepartmentalExam.com',
          description: `Enrollment: ${orderData.course_name}`,
          order_id: orderData.order_id,
          handler: async function (response) {
            // Verify payment on backend
            try {
              const verifyResponse = await axios.post('/payment/verify', {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });
              
              toast.success(`🎉 Enrolled in ${paymentCourse.name} successfully!`);
              setShowPayment(false);
              setPaymentCourse(null);
              fetchEnrolledCourses();
              fetchStats();
              refreshUser();
              
              // Show receipt info
              if (verifyResponse.data.receipt) {
                toast.success(`Receipt: ${verifyResponse.data.receipt.id}`);
              }
            } catch (error) {
              console.error('Payment verification failed:', error);
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: orderData.user_name || user?.name || '',
            email: orderData.user_email || user?.email || '',
          },
          theme: {
            color: '#2563eb'
          },
          modal: {
            ondismiss: function() {
              setLoading(false);
              toast.info('Payment cancelled');
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', function (response) {
          toast.error(`Payment failed: ${response.error.description}`);
          setLoading(false);
        });
        razorpay.open();
      } else {
        // Mock mode - enroll directly for testing
        toast.info('Processing payment (Test Mode)...');
        
        const verifyResponse = await axios.post('/payment/verify', {
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_order_id: orderData.order_id,
          razorpay_signature: 'mock_signature'
        });

        toast.success(`🎉 Enrolled in ${planData.plan_name || 'course'} successfully!`);
        setShowPayment(false);
        setPaymentCourse(null);
        fetchEnrolledCourses();
        fetchStats();
        refreshUser();
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  // Helper function to load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (document.getElementById('razorpay-script')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const handleStartTest = (testId, courseId) => {
    navigate(`/test/${testId}?course_id=${courseId}`);
  };

  // Render Dashboard Home
  const renderHome = () => (
    <div className="space-y-6">
      {/* Welcome Section - Using WelcomeCard component */}
      <WelcomeCard userName={user?.name || user?.phone} />

      {/* Stats Grid - Using StatsCard components */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          icon={BookOpen}
          value={stats?.coursesEnrolled || enrolledCourses.length}
          label="Courses Enrolled"
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatsCard 
          icon={CheckCircle}
          value={stats?.testsCompleted || 0}
          label="Tests Completed"
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatsCard 
          icon={Clock}
          value={stats?.testsPending || 0}
          label="Tests Pending"
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
        <StatsCard 
          icon={TrendingUp}
          value={`${stats?.averageScore || 0}%`}
          label="Average Score"
          iconBgColor="bg-purple-100"
          iconColor="text-blue-700"
        />
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setActiveTab('courses')}
            >
              <BookOpen className="w-8 h-8 text-blue-600" />
              <span>Browse Courses</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => setActiveTab('my-courses')}
            >
              <PlayCircle className="w-8 h-8 text-green-600" />
              <span>Continue Learning</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/profile')}
              data-testid="view-profile-btn"
            >
              <User className="w-8 h-8 text-blue-700" />
              <span>View Profile</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {enrolledCourses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Continue Learning</CardTitle>
            <CardDescription>Pick up where you left off</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {enrolledCourses.slice(0, 3).map((enrollment) => (
                <div 
                  key={enrollment.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setActiveTab('my-courses');
                    fetchCourseStructure(enrollment.id);
                  }}
                >
                  <div className="flex-1">
                    <h4 className="font-semibold">{enrollment.name}</h4>
                    <p className="text-sm text-gray-600">Last accessed: {new Date(enrollment.enrollment.enrolled_at || enrollment.enrollment.purchase_date).toLocaleDateString()}</p>
                    <Progress value={30} className="mt-2 h-2" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render Free Tests Tab - New dedicated section for free mock tests
  const renderFreeTests = () => {
    // Group free tests by course
    const testsByCourse = freeTests.reduce((acc, freeTest) => {
      const courseId = freeTest.course.id;
      if (!acc[courseId]) {
        acc[courseId] = {
          course: freeTest.course,
          tests: []
        };
      }
      acc[courseId].tests.push(freeTest);
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        {/* Header Card */}
        <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              <Gift className="w-5 h-5 sm:w-6 sm:h-6" />
              Free Mock Tests
            </CardTitle>
            <CardDescription className="text-green-100 text-sm">
              Try one free test from each course before purchasing.
            </CardDescription>
          </CardHeader>
        </Card>

        {freeTests.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Gift className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <h3 className="text-base font-semibold text-gray-700 mb-1">No Free Tests Available</h3>
              <p className="text-sm text-gray-500">Check back later for free mock tests.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {Object.values(testsByCourse).map(({ course, tests }) => (
              <Card key={course.id} className="overflow-hidden" data-testid={`free-test-course-${course.id}`}>
                <CardHeader className="bg-gradient-to-r from-gray-50 to-green-50 border-b p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-sm sm:text-base truncate">{course.name}</CardTitle>
                      <CardDescription className="text-xs">{tests.length} free test{tests.length > 1 ? 's' : ''}</CardDescription>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 sm:p-4">
                  <div className="space-y-3">
                    {tests.map((freeTest) => (
                      <div 
                        key={freeTest.test.id}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          freeTest.attempted 
                            ? 'bg-gray-50 border-gray-200' 
                            : 'bg-green-50 border-green-200'
                        }`}
                        data-testid={`free-test-${freeTest.test.id}`}
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2 mb-1">
                              <h4 className="font-semibold text-gray-800 text-sm">{freeTest.test.title}</h4>
                              {freeTest.attempted && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded flex-shrink-0">
                                  Done
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mb-2 truncate">
                              {freeTest.subject.name} • {freeTest.chapter.name}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {freeTest.test.time_limit / 60}m
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {freeTest.test.total_questions} Q
                              </span>
                              {freeTest.best_score !== null && (
                                <span className="flex items-center gap-1 text-green-600 font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  {freeTest.best_score.toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex-shrink-0">
                            {freeTest.attempted ? (
                              <Button 
                                variant="outline"
                                size="sm"
                                className="w-full text-xs"
                                onClick={() => setActiveTab('courses')}
                                data-testid={`purchase-course-${course.id}`}
                              >
                                View Course
                              </Button>
                            ) : (
                              <Button 
                                className="w-full bg-green-600 hover:bg-green-700 text-xs"
                                size="sm"
                                onClick={() => handleStartTest(freeTest.test.id, freeTest.course.id)}
                                data-testid={`start-free-test-${freeTest.test.id}`}
                              >
                                <PlayCircle className="w-3 h-3 mr-1" />
                                Start Test
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render My Courses Tab  
  const renderMyCourses = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl">My Enrolled Courses</CardTitle>
          <CardDescription className="text-sm">Courses you have purchased</CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          {enrolledCourses.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 text-sm mb-3">No courses enrolled yet</p>
              <Button onClick={() => setActiveTab('courses')} size="sm">
                Browse Courses
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {enrolledCourses.map((enrollment) => {
                const expiryDate = new Date(enrollment.enrollment.expiry_date);
                const isExpired = expiryDate < new Date();
                
                return (
                <Card key={enrollment.id} className={`hover:shadow-lg transition-shadow ${isExpired ? 'border-red-300' : ''}`}>
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    {enrollment.thumbnail ? (
                      <div className="w-full h-28 sm:h-36 rounded-lg mb-2 overflow-hidden bg-gray-100 flex items-center justify-center">
                        <img 
                          src={enrollment.thumbnail} 
                          alt={enrollment.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-28 sm:h-36 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                          <BookOpen className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-28 sm:h-36 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg mb-2 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <CardTitle className="text-sm sm:text-base line-clamp-2">{enrollment.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="space-y-2">
                      {/* Plan & Expiry - Compact */}
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Plan:</span>
                        <span className="font-semibold text-blue-600 truncate ml-2">
                          {enrollment.enrollment?.plan_name || 'Standard'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">{isExpired ? 'Expired:' : 'Valid:'}</span>
                        <span className={`font-semibold ${isExpired ? 'text-red-600' : ''}`}>
                          {expiryDate.toLocaleDateString()}
                        </span>
                      </div>

                      {isExpired ? (
                        <div className="p-2 bg-red-50 border border-red-200 rounded-lg mt-2">
                          <p className="text-xs text-red-700">⚠️ Course expired. Re-purchase to continue.</p>
                        </div>
                      ) : (
                        <>
                          <Progress value={enrollment.enrollment?.progress?.completion_percentage || 0} className="h-1.5 mt-2" />
                          <p className="text-xs text-gray-600">
                            {enrollment.enrollment?.progress?.completion_percentage || 0}% 
                            {enrollment.enrollment?.progress?.total_tests > 0 && (
                              <span className="text-gray-400 ml-1">
                                ({enrollment.enrollment?.progress?.completed_tests || 0}/{enrollment.enrollment?.progress?.total_tests})
                              </span>
                            )}
                          </p>
                          <Button 
                            className="w-full mt-2 text-xs h-8"
                            size="sm"
                            onClick={() => fetchCourseStructure(enrollment.id)}
                          >
                            View Content
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Course Structure Dialog - defined separately to be used in render
  const CourseStructureDialog = () => (
    <Dialog open={!!selectedCourse} onOpenChange={() => {setSelectedCourse(null); setCourseStructure(null);}}>
      <DialogContent className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg pr-6">
              {courseStructure ? courseStructure.name : 'Course Content'}
            </DialogTitle>
          </DialogHeader>
          {!courseStructure ? (
            <div className="py-8 text-center">
              <p className="text-gray-600">Loading course content...</p>
            </div>
          ) : courseStructure.subjects && courseStructure.subjects.length > 0 ? (
            <div className="space-y-3">
              {courseStructure.subjects.map((subject, sIndex) => (
                <Accordion key={subject.id} type="single" collapsible>
                  <AccordionItem value={subject.id}>
                    <AccordionTrigger className="hover:no-underline px-2 sm:px-4">
                      <div className="flex items-center gap-2 text-left">
                        <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                        <span className="font-semibold text-sm sm:text-base">{subject.order}. {subject.name}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="ml-2 sm:ml-4 space-y-2">
                        {subject.chapters && subject.chapters.map((chapter, cIndex) => {
                          // Check if chapter is unlocked
                          let isUnlocked = cIndex === 0;
                          if (cIndex > 0 && subject.chapters[cIndex - 1]) {
                            const prevChapter = subject.chapters[cIndex - 1];
                            if (prevChapter.tests && prevChapter.tests.length > 0) {
                              isUnlocked = prevChapter.tests.every(test => test.completed);
                            }
                          }
                          
                          return (
                            <Accordion key={chapter.id} type="single" collapsible>
                              <AccordionItem value={chapter.id} className="border rounded-lg">
                                <AccordionTrigger className="hover:no-underline px-2 sm:px-4 py-2">
                                  <div className="flex items-center gap-2 text-left">
                                    {isUnlocked ? (
                                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    ) : (
                                      <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    )}
                                    <span className="font-medium text-xs sm:text-sm">{chapter.order}. {chapter.name}</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                  {isUnlocked && chapter.tests && chapter.tests.length > 0 ? (
                                    <div className="mx-2 sm:mx-4 space-y-2 pb-2">{chapter.tests.map((test, tIndex) => (
                                    <div key={test.id} className="flex items-center justify-between p-2 bg-white rounded-lg border gap-2">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {test.completed ? (
                                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        ) : test.unlocked ? (
                                          <Circle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        ) : (
                                          <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <p className="font-medium text-gray-800 text-xs sm:text-sm">{test.title}</p>
                                          <div className="flex items-center gap-2 text-xs text-gray-600">
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3 h-3" />
                                              {test.time_limit / 60}m
                                            </span>
                                            {test.best_score !== null && (
                                              <span className="text-green-600 font-semibold">
                                                Best: {test.best_score.toFixed(2)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <Button 
                                        size="sm"
                                        onClick={() => handleStartTest(test.id, selectedCourse)}
                                        disabled={!test.unlocked}
                                        className={`flex-shrink-0 h-8 px-2 sm:px-3 text-xs sm:text-sm ${test.completed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                      >
                                        <PlayCircle className="w-3 h-3 mr-1" />
                                        {test.completed ? 'Retry' : test.unlocked ? 'Start' : 'Locked'}
                                      </Button>
                                    </div>
                                  ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500 px-4 pb-2">
                                      {isUnlocked ? 'No tests available in this chapter.' : 'Complete previous chapter to unlock.'}
                                    </p>
                                  )}
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-600">
              <p>No content available for this course yet.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );

  // Render Available Courses Tab
  const renderCourses = () => (
    <div className="space-y-4">
      {/* Departments Section */}
      {!selectedDepartment && departments.length > 0 && (
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Browse by Department</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Select a department to view courses</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
              {departments.map((dept) => (
                <Card 
                  key={dept.id}
                  className="hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => fetchCoursesByDepartment(dept.id)}
                >
                  <CardContent className="p-3 sm:pt-6 flex flex-col items-center text-center space-y-2">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-50 rounded-lg flex items-center justify-center overflow-hidden">
                      {dept.logo ? (
                        <img src={dept.logo} alt={dept.name} className="w-full h-full object-cover" />
                      ) : (
                        <BookOpen className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" />
                      )}
                    </div>
                    <h3 className="font-semibold text-xs sm:text-sm">{dept.name}</h3>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Courses Section */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <CardTitle className="text-base sm:text-lg">
                {selectedDepartment 
                  ? `${departments.find(d => d.id === selectedDepartment)?.name || ''}`
                  : 'All Courses'}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Browse and enroll in courses</CardDescription>
            </div>
            {selectedDepartment && (
              <Button 
                variant="outline" 
                size="sm"
                className="text-xs self-start"
                onClick={() => {
                  setSelectedDepartment(null);
                  fetchCourses();
                }}
              >
                ← Back
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {courses.map((course) => {
              const isEnrolled = enrolledCourses.some(e => e.id === course.id);
              return (
                <Card key={course.id} className="hover:shadow-xl transition-all">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    {course.thumbnail ? (
                      <div className="w-full h-28 sm:h-36 rounded-lg mb-2 overflow-hidden bg-gray-100 flex items-center justify-center">
                        <img 
                          src={course.thumbnail} 
                          alt={course.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-28 sm:h-36 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                          <BookOpen className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-28 sm:h-36 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg mb-2 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <CardTitle className="text-sm sm:text-base line-clamp-2">{course.name}</CardTitle>
                    <CardDescription className="text-xs line-clamp-1 mt-1">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <div className="space-y-2">
                      {(() => {
                        const plans = coursePricingPlans[course.id] || [];
                        const activePlans = plans.filter(p => p.is_active);
                        
                        if (activePlans.length > 0) {
                          const cheapest = activePlans.reduce((min, p) => p.price < min.price ? p : min);
                          const mostExpensive = activePlans.reduce((max, p) => p.price > max.price ? p : max);
                          
                          return (
                            <div>
                              {activePlans.length > 1 ? (
                                <span className="text-lg sm:text-xl font-bold text-blue-600">
                                  ₹{cheapest.price} - ₹{mostExpensive.price}
                                </span>
                              ) : (
                                <span className="text-lg sm:text-xl font-bold text-blue-600">
                                  ₹{cheapest.price}
                                </span>
                              )}
                              <p className="text-xs text-gray-500">
                                From ₹{(cheapest.price / (cheapest.duration_in_days / 30)).toFixed(0)}/mo
                              </p>
                            </div>
                          );
                        } else if (course.price > 0) {
                          return (
                            <div>
                              <span className="text-lg sm:text-xl font-bold text-blue-600">₹{course.price}</span>
                              <p className="text-xs text-gray-500">Standard plan</p>
                            </div>
                          );
                        } else {
                          // Hide price if 0 and no plans
                          return (
                            <div className="text-sm text-gray-600">
                              <p>Contact admin for pricing</p>
                            </div>
                          );
                        }
                      })()}
                      <Button 
                        className={`w-full text-xs h-8 ${isEnrolled ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        size="sm"
                        onClick={() => handleViewCourseDetails(course)}
                      >
                        {isEnrolled && <CheckCircle className="w-3 h-3 mr-1" />}
                        View Course
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Course Details Dialog */}
      <Dialog open={showCourseDetails} onOpenChange={setShowCourseDetails}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg pr-6">{viewCourse?.name}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {viewCourse?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Course Image */}
            {viewCourse?.thumbnail ? (
              <div className="w-full h-32 sm:h-48 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                <img 
                  src={viewCourse.thumbnail} 
                  alt={viewCourse.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-32 sm:h-48 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center" style={{display: 'none'}}>
                  <BookOpen className="w-10 h-10 text-white" />
                </div>
              </div>
            ) : (
              <div className="w-full h-32 sm:h-48 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
            )}}

            {/* Course Details */}
            {(() => {
              const plans = coursePricingPlans[viewCourse?.id] || [];
              const activePlans = plans.filter(p => p.is_active);
              
              if (activePlans.length > 0) {
                const cheapest = activePlans.reduce((min, p) => p.price < min.price ? p : min);
                const mostExpensive = activePlans.reduce((max, p) => p.price > max.price ? p : max);
                
                return (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Price</p>
                        {activePlans.length > 1 ? (
                          <p className="text-2xl font-bold text-blue-600">₹{cheapest.price} - ₹{mostExpensive.price}</p>
                        ) : (
                          <p className="text-2xl font-bold text-blue-600">₹{cheapest.price}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          from ₹{(cheapest.price / (cheapest.duration_in_days / 30)).toFixed(0)}/month
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Duration Options</p>
                        <p className="text-xl font-bold text-gray-800">{activePlans.length} Plan{activePlans.length > 1 ? 's' : ''}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {Math.floor(cheapest.duration_in_days / 30)} - {Math.floor(mostExpensive.duration_in_days / 30)} months
                        </p>
                      </div>
                    </div>
                  </>
                );
              } else if (viewCourse?.price > 0) {
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Price</p>
                      <p className="text-2xl font-bold text-blue-600">₹{viewCourse?.price}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Validity</p>
                      <p className="text-2xl font-bold text-gray-800">5 months</p>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600 mb-1">Pricing</p>
                    <p className="text-lg font-semibold text-gray-800">Contact admin for pricing</p>
                  </div>
                );
              }
            })()}

            {/* Course Features */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">What you'll get:</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Access to all subjects and chapters
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Unlimited mock tests
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Detailed performance analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {(() => {
                    const plans = coursePricingPlans[viewCourse?.id] || [];
                    const activePlans = plans.filter(p => p.is_active);
                    if (activePlans.length > 0) {
                      return `Flexible subscription plans (${activePlans.length} options)`;
                    }
                    return '5 months validity from purchase date';
                  })()}
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowCourseDetails(false)}>
                Close
              </Button>
              {(() => {
                const enrollment = enrolledCourses.find(e => e.id === viewCourse?.id);
                const isExpired = enrollment && new Date(enrollment.enrollment.expiry_date) < new Date();
                
                if (enrollment && !isExpired) {
                  return (
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleViewEnrolledCourse(viewCourse?.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Go to Course
                    </Button>
                  );
                } else {
                  return (
                    <Button 
                      className="flex-1"
                      onClick={() => handlePurchaseCourse(viewCourse)}
                    >
                      {isExpired ? 'Re-purchase Course' : 'Purchase Course'}
                    </Button>
                  );
                }
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
            <DialogDescription>
              Select a subscription plan for: {paymentCourse?.name}
            </DialogDescription>
          </DialogHeader>
          
          <PlanSelector
            courseId={paymentCourse?.id}
            courseName={paymentCourse?.name}
            coursePrice={paymentCourse?.price || 0}
            onEnroll={handlePayment}
          />
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render Profile Tab
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50">
      {/* Header - Using DashboardHeader component */}
      <DashboardHeader 
        onLogout={onLogout} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 top-[57px] sm:top-[65px]">
          <div className="absolute inset-0 bg-gray-900 bg-opacity-50" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute left-0 w-64 bg-white h-full shadow-xl p-4">
            <nav className="space-y-2">
              {[
                { id: 'home', label: 'Dashboard', icon: Home },
                { id: 'my-courses', label: 'My Courses', icon: BookOpen },
                { id: 'free-tests', label: 'Free Tests', icon: Gift },
                { id: 'courses', label: 'Browse Courses', icon: Award },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeTab === item.id
                        ? 'bg-blue-100 text-blue-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`mobile-nav-${item.id}`}
                  >
                    <Icon className={`w-5 h-5 ${activeTab === item.id ? 'text-blue-700' : 'text-gray-500'}`} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Desktop Tabs */}
        <div className="hidden lg:block">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 bg-gray-100">
              <TabsTrigger 
                value="home"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                data-testid="tab-home"
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="my-courses"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                data-testid="tab-my-courses"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                My Courses
              </TabsTrigger>
              <TabsTrigger 
                value="free-tests"
                className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
                data-testid="tab-free-tests"
              >
                <Gift className="w-4 h-4 mr-2" />
                Free Tests
              </TabsTrigger>
              <TabsTrigger 
                value="courses"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                data-testid="tab-courses"
              >
                <Award className="w-4 h-4 mr-2" />
                Browse Courses
              </TabsTrigger>
            </TabsList>

            <TabsContent value="home">{renderHome()}</TabsContent>
            <TabsContent value="my-courses">{renderMyCourses()}</TabsContent>
            <TabsContent value="free-tests">{renderFreeTests()}</TabsContent>
            <TabsContent value="courses">{renderCourses()}</TabsContent>
          </Tabs>
        </div>

        {/* Mobile Content */}
        <div className="lg:hidden">
          {activeTab === 'home' && renderHome()}
          {activeTab === 'my-courses' && renderMyCourses()}
          {activeTab === 'free-tests' && renderFreeTests()}
          {activeTab === 'courses' && renderCourses()}
        </div>
      </div>
      
      {/* Course Structure Dialog - Rendered globally */}
      <CourseStructureDialog />
    </div>
  );
};

export default StudentDashboard;
